import type { ChangeEventHandler } from "react";
import { useState } from "react";

import { UiWarningIcon } from "@jti/brand-icons/ui-warning";
import { RadioContainer } from "@jti/core-components";
import type { MagentoShippingMethod } from "@jti/magento-types";
import { useTranslations } from "@jti/ui";

import { isDeliveryOptionSelectable } from "@checkout-v2/shared/utils/prepareDeliveryOptions";

import { useShippingOptionLabels } from "../../hooks";
import { ShippingOptionLabel } from "../ShippingOptionLabel";

import styles from "./DeliveryOptionsList.module.scss";

const VISIBLE_OPTIONS_LIMIT = 3;

type DeliveryOptionsListProps = {
  options: MagentoShippingMethod[];
  isSubscription?: boolean;
  value: string;
  onChange: ChangeEventHandler<HTMLInputElement>;
  name: string;
  error?: boolean;
  errorText?: string;
};

export const DeliveryOptionsList = ({
  options,
  isSubscription,
  value,
  onChange,
  name,
  error,
  errorText,
}: DeliveryOptionsListProps) => {
  const { __ } = useTranslations();
  const getShippingOptionLabels = useShippingOptionLabels();
  const [showAll, setShowAll] = useState(false);

  const hasMoreOptions = options.length > VISIBLE_OPTIONS_LIMIT;
  const visibleOptions = hasMoreOptions && !showAll ? options.slice(0, VISIBLE_OPTIONS_LIMIT) : options;
  const errorId = `${name}-error`;
  const hasError = !!(error && errorText);

  if (options.length === 1) {
    const [option] = options;

    if (!isDeliveryOptionSelectable(option, isSubscription)) {
      const { title, subtitle } = getShippingOptionLabels(option);

      return (
        <div className={styles.container}>
          <RadioContainer
            name={name}
            value={option.methodCode}
            checked={value === option.methodCode}
            onChange={onChange}
            isError={error}
            disabled
            label={title}
            description={subtitle}
          />
        </div>
      );
    }

    return (
      <div className={styles.container}>
        <div className={styles.singleOptionContainer}>
          <ShippingOptionLabel magentoShippingMethod={option} />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <fieldset className={styles.group} aria-describedby={hasError ? errorId : undefined}>
        <legend className={styles.legend}>{__("cart.checkout.shipping_options.title")}</legend>
        {visibleOptions.map((option) => {
          const { title, subtitle } = getShippingOptionLabels(option);
          const { methodCode } = option;

          return (
            <RadioContainer
              key={methodCode}
              name={name}
              value={methodCode}
              checked={value === methodCode}
              onChange={onChange}
              isError={error}
              disabled={!isDeliveryOptionSelectable(option, isSubscription)}
              label={title}
              description={subtitle}
            />
          );
        })}
      </fieldset>
      {error && errorText && (
        <div className={styles.errorText} id={errorId}>
          <UiWarningIcon className={styles.errorIcon} />
          {errorText}
        </div>
      )}
      {hasMoreOptions && (
        <button type="button" className={styles.toggleButton} onClick={() => setShowAll((prev) => !prev)}>
          {showAll ? __("cart.checkout.shipping_options.see_less") : __("cart.checkout.shipping_options.see_more")}
        </button>
      )}
    </div>
  );
};
